import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { get, pick } from 'lodash';
import { Alert } from 'react-bootstrap';
import AlertPopup from '../../../../components/react/alert_popup/alert_popup';
import { GENERIC_ERROR_MESSAGE } from '../../../../components/react/alert_popup/constants';
import AutocompleteBootstrap5 from '../../../../components/react/autocompletes/autocomplete_bootstrap_5';
import { autocompleteConsultantsPath } from '../../../../utilities/api_relative_paths';
import {
  addUserToOrganization,
  modifyUserInOrganization,
} from '../../../../services/organizations/organizations.service';

import styles from './modify_organization_staff.module.scss';

const FIELD_ENTRY_BEM_PREFIX = 'modify-organization-staff-field__';

const ModifyOrganizationStaff = (props) => {
  const [alertData, setAlertData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(true);
  const [editedConsultant, setEditedConsultant] = useState(
    get(props, 'consultant.id') && {
      id: props.consultant.id,
      data: { headline: props.consultant.headline },
      text: props.consultant.full_name,
    }
  );

  const { organizationId, exit } = props;

  const [fields, setFields] = useState(pick(props, ['name', 'phone', 'email', 'job_title', 'id']));

  useEffect(() => setFields({ ...fields, type: props.type }), [props.type]);

  const contactId = props.id;
  const title = contactId ? 'Edit Staff' : 'Add Staff';
  const alertMessage = contactId
    ? `Edit official points of contact and staff to let people know
    how to best get in touch with your organization.`
    : `Search for your staff profiles to complete the information,
    or define a new contact from scratch.`;

  const onSubmit = (event) => {
    event.preventDefault();

    setLoading(true);
    const operation = contactId ? modifyUserInOrganization : addUserToOrganization;
    const body = fields;
    // contact was or will be linked to a consultant
    const associatedWithConsultant = props.consultant || editedConsultant;
    if (associatedWithConsultant) body.consultant_id = get(editedConsultant, 'id', null);
    operation(organizationId, body, contactId)
      .then((field) => {
        const successMessage = ` ${get(field, 'consultant.full_name', field.name)} was successfully
          ${operation === modifyUserInOrganization ? 'modified.' : 'added to the staff.'}`;
        setAlertData({ type: 'success', message: successMessage });
        exit();
      })
      .catch((error) => {
        let message;

        if (error?.message) {
          message = error.message;
        } else if (error?.errors && error?.errors?.length) {
          message = error.errors.map((e) => e.message || e).join(', ');
        } else {
          message = GENERIC_ERROR_MESSAGE;
        }

        setAlertData({ type: 'error', message });
        setLoading(false);
      });
  };

  /**
   * Creates a label/input for the form
   *
   * @param {string} field - the data to display an manipulate
   * @param {string} text - the text to display for the label
   * @param {boolean} [required] - should the field be shown as required
   * @param {boolean} [type] - type of input
   * @returns {Node} the input and label to display
   */
  const displayFieldEntry = (field, text, required, type = 'text') => {
    const id = FIELD_ENTRY_BEM_PREFIX + field;
    const label = (
      <label htmlFor={id} className='form-label'>
        {text}
        {required && <span className='text-orange-devex'> *</span>}
      </label>
    );
    const input = (
      <input
        data-testid={field}
        id={id}
        required={required}
        type={type}
        value={get(fields, field) || ''}
        className='form-control'
        onChange={(e) => setFields({ ...fields, [field]: e.target.value })}
        name={field}
        placeholder={text}
      />
    );
    return (
      <div className={`my-25 ${styles.contactField}`}>
        <div className={styles.labelContainer}>{label}</div>
        <div className={styles.inputContainer}>{input}</div>
      </div>
    );
  };

  const selectTypeDisplay = (
    <div className={`my-25 ${styles.contactField}`}>
      <div className={styles.labelContainer}>
        <label className='form-label'>
          Type <span className='text-orange-devex'> *</span>
        </label>
      </div>
      <div className={styles.inputContainer}>
        <div className='select-wrapper'>
          <select
            value={fields.type}
            id={FIELD_ENTRY_BEM_PREFIX + 'type'}
            title='Type of Staff'
            onChange={(e) => setFields({ ...fields, type: e.target.value })}
            className='form-control'
          >
            <option value='current'>Current staff</option>
            <option value='former'>Former staff</option>
            <option value='official'>Official staff</option>
          </select>
        </div>
      </div>
    </div>
  );

  const infoDisplay = (
    <Alert
      show={showAlert}
      onClose={() => setShowAlert(false)}
      dismissible
      variant='info'
      key='info'
    >
      <div className='d-flex'>
        <i className='fal icon-info-circle me-15 text-blue' />
        <p className='fs-7 m-0 text-blue'>{alertMessage}</p>
      </div>
    </Alert>
  );

  const cancelButtonDisplay = (
    <button className='btn btn-outline-secondary me-25' type='button' onClick={exit}>
      <i className='fal icon-times-circle' /> Cancel
    </button>
  );

  const submitButtonDisplay = (
    <button className='btn btn-outline-primary' type='submit' disabled={loading}>
      <i className='fal icon-save me-15' />
      {loading ? 'Loading...' : contactId ? 'Save changes' : 'Add Staff'}
    </button>
  );

  // The fields "name", "email" and "job title" are not necessary when using "Devex profile"
  const handleSelectDevexProfile = (selected) => {
    setEditedConsultant(selected);
    setFields(pick(fields, ['type', 'phone']));
  };

  const consultantTypeahead = (
    <div className={`my-25 ${styles.contactField}`}>
      <div className={styles.labelContainer}>
        <label className='form-label'>Devex profile</label>
      </div>
      <div className={styles.inputContainer}>
        <AutocompleteBootstrap5
          inputProps={{ className: 'form-control' }}
          filter={{ company_id: organizationId, by_name: true }}
          id='consultantAutocomplete'
          labelKey={({ text, data }) => [text, data.email].filter(Boolean).join(' - ')}
          placeholder='Search profiles on this organization'
          apiPath={autocompleteConsultantsPath}
          selected={editedConsultant ? [editedConsultant] : []}
          onSelect={(selected) => handleSelectDevexProfile(selected)}
        />
      </div>
    </div>
  );

  return (
    <>
      <AlertPopup alertData={alertData} setAlertData={setAlertData} />
      <div
        className={`py-45 ${styles.formStaffWrapper}`}
        data-testid='modify-organization-staff-component'
      >
        <form className='form-group container' onSubmit={onSubmit}>
          <h2 className='mb-3 py-1 border-bottom'>{title}</h2>
          <div className='d-flex flex-column pb-35'>
            {infoDisplay}
            {consultantTypeahead}
            {!editedConsultant && displayFieldEntry('name', 'Name', true)}
            {!editedConsultant && displayFieldEntry('job_title', 'Job Title')}
            {!editedConsultant && displayFieldEntry('email', 'Email', true, 'email')}
            {displayFieldEntry('phone', 'Phone')}
            {selectTypeDisplay}
          </div>
          <div className={`d-flex justify-content-end mt-25 ${styles.buttonsWrapper}`}>
            {cancelButtonDisplay}
            {submitButtonDisplay}
          </div>
        </form>
      </div>
    </>

  );
};

ModifyOrganizationStaff.propTypes = {
  id: PropTypes.number,
  phone: PropTypes.string,
  email: PropTypes.string,
  name: PropTypes.string,
  job_title: PropTypes.string,
  canEdit: PropTypes.bool,
  organizationId: PropTypes.string,
  exit: PropTypes.func.isRequired,
  type: PropTypes.string,
  consultant: PropTypes.shape({
    id: PropTypes.number,
    full_name: PropTypes.string,
    headline: PropTypes.string,
  }),
};

export default ModifyOrganizationStaff;
